import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { AutoFitType, TextDirectionType, WrapType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { TEXT_BASELINE_OFFSET } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { toJS, isObservableObject } from 'mobx';
import { setImageValue } from './element-menu-values-setter';
import { DEFAULT_COLUMN_NUMBER, DEFAULT_COLUMN_SPACE } from '@src/slide/application/service/column-dialog-manager/core/constant';
import { BuiltInBulletCharacterConfigs } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/character-type';
import {
    AlignType,
    IndentType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';

/**
 * 以下逻辑由shape.model迁移而来
 */

const DEFAULT_TEXT_VALUE = {
    [ActionType.BOLD]: false,
    [ActionType.ITALIC]: false,
    [ActionType.UNDERLINE]: false,
    [ActionType.DELETELINE]: false,
    [ActionType.TEXT_INDENT]: 0,
    [ActionType.SUBSCRIPT]: false,
    [ActionType.SUPERSCRIPT]: false,
    [ActionType.COLUMN_NUMBER]: DEFAULT_COLUMN_NUMBER,
    [ActionType.COLUMN_SPACE]: DEFAULT_COLUMN_SPACE,
};

const MENU_NEED_ACTION_TYPE = [
    ActionType.BOLD, ActionType.ITALIC, ActionType.UNDERLINE, ActionType.DELETELINE,
    ActionType.FORECOLOR, ActionType.BACKCOLOR, ActionType.VERTICAL_ALIGN, ActionType.HORIZONTAL_ALIGN,
    AlignType.HORIZONTAL_LEFT, AlignType.HORIZONTAL_RIGHT, AlignType.HORIZONTAL_CENTER, AlignType.HORIZONTAL_JUSTIFY,
    AlignType.HORIZONTAL_DISTRIBUTE, ActionType.FONTSIZE, ActionType.FONTSIZE_UP, ActionType.FONTSIZE_DOWN,
    ActionType.FONT, ActionType.ROW_SPACE, ActionType.LINE_HEIGHT_IS, ActionType.ROW_SPACE_ABSOLUTE,
    ActionType.ITEMNUMBER, ActionType.DIGITNUMBER, ActionType.BULLET_COLOR, ActionType.BULLET_SIZE,
    ActionType.BULLET_START_NUMBER, ActionType.BULLET_CUSTOM_TEXT, ActionType.BULLET_CUSTOM_IMAGE,
    ActionType.INDENT, ActionType.TEXT_INDENT, ActionType.ENTER_EDITOR,
    ActionType.UNDO, ActionType.REDO, ActionType.LINK, ActionType.TEXT_ADJUST, ActionType.MARGIN_LEFT,
    ActionType.SPACE_BEFORE, ActionType.SPACE_AFTER, ActionType.TAB_LIST, ActionType.TAB_SIZE,
    ActionType.PADDING_ADJUST, IndentType.INDENT_INCREASE, IndentType.INDENT_REDUCE, ActionType.FONT_EFFECT,
    ActionType.FONT_FILL, ActionType.FONT_BORDER,
];

/**
 * 菜单涉及到的属性对照转换表， 将model style上的属性值转换为menu对应的
 */
const STYLE_TO_MENU_MAP = {
    fontWeight(val) {
        return {
            id: ActionType.BOLD,
            value: val === 'bold',
        };
    },
    fontStyle(val) {
        return {
            id: ActionType.ITALIC,
            value: val === 'italic',
        };
    },
    textDecoration(val) {
        return {
            id: ActionType.UNDERLINE,
            value: val === 'underline',
        };
    },
    del(val) {
        return {
            id: ActionType.DELETELINE,
            value: val === 'sng',
        };
    },
    [TEXT_BASELINE_OFFSET](val) {
        if (val > 0) {
            return {
                id: ActionType.SUPERSCRIPT,
                value: true,
            };
        }
        if (val < 0) {
            return {
                id: ActionType.SUBSCRIPT,
                value: true,
            };
        }
        return {};
    },
    fontSize(val) {
        return {
            id: ActionType.FONTSIZE,
            value: `font-size-${val}pt`,
        };
    },
    fontFamily(val) {
        // 某些不支持的字体，属性会是undefined，做一个保护处理
        let v = (val?.split(',')) || '';
        if (v.length) v = v[0];
        return {
            id: ActionType.FONT,
            value: v,
        };
    },
    color(val) {
        return {
            id: ActionType.FORECOLOR,
            value: val,
        };
    },
    backgroundColor(val) {
        return {
            id: ActionType.BACKCOLOR,
            value: val,
        };
    },
    alignItems(val) {
        function getVal(raw) {
            switch (raw) {
                case 'flex-start':
                    return AlignType.VERTICAL_TOP;
                case 'center':
                    return AlignType.VERTICAL_CENTER;
                case 'flex-end':
                    return AlignType.VERTICAL_BOTTOM;
                default:
                    return;
            }
        }

        return {
            id: ActionType.VERTICAL_ALIGN,
            value: getVal(val),
        };
    },
    textAlign(val) {
        function getVal(raw) {
            switch (raw) {
                case 'left':
                    return AlignType.HORIZONTAL_LEFT;
                case 'center':
                    return AlignType.HORIZONTAL_CENTER;
                case 'right':
                    return AlignType.HORIZONTAL_RIGHT;
                case 'justify':
                    return AlignType.HORIZONTAL_JUSTIFY;
                case 'distribute':
                    return AlignType.HORIZONTAL_DISTRIBUTE;
                default:
                    return;
            }
        }

        return {
            id: ActionType.HORIZONTAL_ALIGN,
            value: getVal(val),
        };
    },
    PPTLineHeight(val) {
        return {
            id: ActionType.ROW_SPACE,
            value: val,
        };
    },
    lineHeight(val) {
        return {
            id: ActionType.ROW_SPACE_ABSOLUTE,
            value: val,
        };
    },
    lineHeightIs(val) {
        return {
            id: ActionType.LINE_HEIGHT_IS,
            value: val,
        };
    },
    marginLeft(val) {
        return {
            id: ActionType.MARGIN_LEFT,
            value: val,
        };
    },
    textIndent(val) {
        return {
            id: ActionType.TEXT_INDENT,
            value: val,
        };
    },
    paraPaddingTop(val) {
        return {
            id: ActionType.SPACE_BEFORE,
            value: val,
        };
    },
    paraPaddingBottom(val) {
        return {
            id: ActionType.SPACE_AFTER,
            value: val,
        };
    },
    tabList(val) {
        return {
            id: ActionType.TAB_LIST,
            value: isObservableObject(val) ? toJS(val) : val,
        };
    },
    tabSize(val) {
        return {
            id: ActionType.TAB_SIZE,
            value: val,
        };
    },
    buClr(value) {
        if (value?.color) {
            return {
                id: ActionType.BULLET_COLOR,
                value: value?.color,
            };
        }
        return {};
    },
    buSzPct(size) {
        if (typeof size === 'number') {
            return {
                id: ActionType.BULLET_SIZE,
                value: size,
            };
        }
        return {};
    },
    buAutoNum(value) {
        const { startAt, type } = value || {};
        const result: Array<{ id: string, value: any }> = [];
        if (startAt) {
            result.push({
                id: ActionType.BULLET_START_NUMBER,
                value: startAt,
            });
        }
        if (type) {
            result.push({
                id: ActionType.DIGITNUMBER,
                value: type,
            });
        }
        return result;
    },
    character_bullet_char(value, allStyle) {
        if (value) {
            if (value === 'multi') {
                // 如果选中多个值不一样，直接返回
                return {};
            }
            const buFont = allStyle.find(style => style.id === 'buFont');
            const typeface = buFont?.value?.typeface;
            if (typeface) {
                const characterConfig = BuiltInBulletCharacterConfigs.find(item => (
                    item.typeface === typeface && item.character_bullet_char === value
                ));
                if (characterConfig) {
                    return {
                        id: ActionType.ITEMNUMBER,
                        value: characterConfig.type,
                    };
                }
            }
            return {
                id: ActionType.BULLET_CUSTOM_TEXT,
                value,
            };
        }
    },
    buBlip(value) {
        if (value?.blip?.src) {
            return {
                id: ActionType.BULLET_CUSTOM_IMAGE,
                value: value?.blip.src,
            };
        }
        return {};
    },
    [ActionType.TEXT_ADJUST](val) {
        return {
            id: ActionType.TEXT_ADJUST,
            value: val,
        };
    },
    effect(val) {
        return {
            id: ActionType.FONT_EFFECT,
            value: val,
        };
    },
    [ActionType.FONT_FILL](val) {
        return {
            id: ActionType.FONT_FILL,
            value: isObservableObject(val) ? toJS(val) : val,
        };
    },
    border(val) {
        return {
            id: ActionType.FONT_BORDER,
            value: isObservableObject(val) ? toJS(val) : val,
        };
    },
    [ActionType.COLUMN_NUMBER](value: number) {
        return {
            id: ActionType.COLUMN_NUMBER,
            value,
        };
    },
    [ActionType.COLUMN_SPACE](value: number) {
        return {
            id: ActionType.COLUMN_SPACE,
            value,
        };
    },
    tdColor(val) {
        return {
            id: ActionType.FONT_THEME_COLOR,
            value: val,
        };
    },
};

/**
 * 获取model上全量的style数据列表
 * @param model
 */
function getModelAllStyleList(model: IShapeModel): { id: string, value: any }[] {
    let styles = model.selectionStyles || {};
    if (model.editorInstance && !model.editorInstance?.isEditing) {
        styles = model.editorInstance?.getAllStylesSync() || {};
    }

    // 去掉某些项的multi，这些项是multi会造成toolbar显示错误
    // 目前情况下只有字体字号是不需要判断的
    [
        'alignItems',
        'backgroundColor',
        'displayFontSize',
        'fonts',
        'rawFontSize',
        'textDecoration',
        TEXT_BASELINE_OFFSET,
    ].forEach((key) => {
        if (styles[key] === 'multi') delete styles[key];
    });

    return Object.keys(styles).map(key => ({
        id: key,
        value: styles[key],
    }));
}

/**
 * 将model全量的style数据列表筛选并转换为菜单需要的数据列表
 * @param styleList
 */
function convertStyleToMenu(styleList): { id: string, value: any }[] {
    return styleList
        .filter(style => STYLE_TO_MENU_MAP[style.id])
        .reduce((result, style) => {
            const actionTypeValueObj = STYLE_TO_MENU_MAP[style.id](style.value, styleList);
            if (!actionTypeValueObj) {
                window?.log?.error(`[Data Error] convertStyleToMenu to null, raw style: ${JSON.stringify(style)}`);
            }
            if (Array.isArray(actionTypeValueObj)) {
                return result.concat(actionTypeValueObj);
            }
            return result.concat([actionTypeValueObj]);
        }, [])
        .filter(item => item);
}

/**
 * 获取当前model所有的菜单属性对象
 * @param model
 */
export function getMenuValues(model: IShapeModel): { [key: string]: any } {
    // 获取style上的全量属性
    const allStyleList = getModelAllStyleList(model);
    // 筛选出部分属性转换为菜单所需格式
    const actionTypeInStyle = convertStyleToMenu(allStyleList);
    // 将上面属性数组合并成对象
    const value = {};
    actionTypeInStyle.forEach((obj) => {
        value[obj.id] = obj.value;
    });
    // 合并一些字体外的属性
    value[ActionType.SIZE_ADJUST] = model.getLock().getLockInfo();
    value[ActionType.FILLCOLOR] = model.getBackgroundFill?.().getFillInfo();
    value[ActionType.EFFECT] = model.getEffect?.().getEffectCollection();
    value[ActionType.BORDERCOLOR] = model.getBorder?.().getBorderInfo();
    value[ActionType.TEXT_ADJUST] = {
        autoFitType: model?.textBodyStyle?.autoFitType || AutoFitType.NO_AUTO_FIT,
        autoBreakType: model?.textBodyStyle?.bodyStyle?.whiteSpace || WrapType.WRAP,
        rawData: model?.textBodyStyle?.bodyStyle,
    };
    value[ActionType.TEXT_DIRECTION] = model?.textBodyStyle?.wrapperStyle?.textDirection
    || TextDirectionType.HORIZONTAL;
    // 补充必须属性
    MENU_NEED_ACTION_TYPE.forEach((p) => {
        value[p] = value[p] || undefined;
    });
    // 补充默认属性
    Object.keys(DEFAULT_TEXT_VALUE).forEach((key) => {
        if (value[key] === undefined) {
            value[key] = DEFAULT_TEXT_VALUE[key];
        }
    });
    setImageValue(value, model);
    return value;
}

